<template>
<div class="price-container">
    <div v-if="priceString" class="sale-price">
        {{ priceString }}
    </div>
    <div v-if="discounted && comparePrice" class="compare-price">
        {{ compareAtPriceString }}
    </div>
</div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { Shopify } from '@jackboxgames/shopify'
import { formatPrice } from '$services/storefronts/storefronts'

const props = defineProps({
    product: Object as PropType<Shopify.Product | null>
})

const app = useNuxtApp()
const selectedVariant = computed(() => props.product?.variants?.[0] || null)
const price = computed(() => priceObj())
const comparePrice = computed(() => priceObj(true))
const maxPrice = computed(() => priceObj(false, true))
const maxComparePrice = computed(() => priceObj(true, true))

const discounted = computed(() => {
    const p = price.value
    const cp = comparePrice.value
    return p && cp && Number(p.amount) < Number(cp.amount)
})

const priceString = computed(() => {
    const p = price.value
    if (!p) return undefined
    const mp = maxPrice.value
    if (!mp || mp.amount === p.amount) return formatPriceHelper(p)
    return `${formatPriceHelper(p)} - ${formatPriceHelper(mp)}`
})

const compareAtPriceString = computed(() => {
    const p = comparePrice.value
    if (!p) return undefined
    const mp = maxComparePrice.value
    if (!mp || mp.amount === p.amount) return formatPriceHelper(p)
    return `${formatPriceHelper(p)} - ${formatPriceHelper(mp)}`
})

function priceObj(compare = false, isMax = false): Shopify.Price | undefined {
    const v = selectedVariant.value
    if (v) {
        return compare ? v.compareAtPrice : v.price
    }
    const p = props.product
    const range = compare ? p?.compareAtPriceRange : p?.priceRange
    return isMax ? range?.maxVariantPrice : range?.minVariantPrice
}

function formatPriceHelper(price: Shopify.Price): string {
    return formatPrice(price.amount, price.currencyCode, app.$i18n.locale)
}
</script>

<style scoped lang="scss">
.price-container {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 1;
    color: var(--neutral-900);
    margin-left: 4px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;

    @media screen and (min-width: 200px) and (max-width: 439px){
        font-size: 12px;
    }

    .sale-price {
        font-size: 14px;
        font-weight: 700;
        color: var(--neutral-900);

        @media screen and (min-width: 200px) and (max-width: 439px){
            font-size: 12px;
        }
    }

    .compare-price {
        font-size: 11px;
        color: var(--neutral-600);
        text-decoration: line-through;
        margin-top: 2px;

        @media screen and (min-width: 200px) and (max-width: 439px){
            font-size: 10px;
        }
    }
}
</style>
